import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import type { ContentListItemDTO } from "../../shared/dto";
import { uploadsUrl } from "../lib/uploader";
import { SectionHeading, Loading, EmptyState } from "../components/ui";

const CONTENT_TYPE_LABELS: Record<string, string> = {
  article: "記事",
  video: "動画",
  pdf: "PDF",
  external_link: "外部リンク",
};
const CONTENT_TYPE_ICON: Record<string, string> = {
  article: "📄",
  video: "🎥",
  pdf: "📑",
  external_link: "🔗",
};

interface Category {
  code: string;
  name: string;
}

export function ContentsPage() {
  const [items, setItems] = useState<ContentListItemDTO[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);

  // カテゴリ一覧 (フィルタ用) は初回のみ取得
  useEffect(() => {
    api
      .get<{ items: Category[] }>("/contents/categories")
      .then((r) => setCategories(r.items))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = category ? `?category=${encodeURIComponent(category)}` : "";
    api
      .get<{ items: ContentListItemDTO[] }>(`/contents${qs}`)
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  }, [category]);

  const catName = (code: string | null) =>
    code ? categories.find((x) => x.code === code)?.name ?? code : null;

  return (
    <div>
      <SectionHeading level={2}>会員限定コンテンツ</SectionHeading>
      {categories.length > 0 && (
        <div
          className="my-4 flex flex-wrap gap-2"
          role="group"
          aria-label="カテゴリで絞り込み"
        >
          {[{ code: "", name: "すべて" }, ...categories].map((cat) => {
            const selected = category === cat.code;
            return (
              <button
                key={cat.code || "all"}
                type="button"
                onClick={() => setCategory(cat.code)}
                className={`inline-flex items-center text-xs px-4 py-1.5 [@media(pointer:coarse)]:min-h-[40px] rounded-full font-semibold transition-colors ${
                  selected
                    ? "bg-tora text-white"
                    : "bg-white border border-tora-ink/20 text-tora-ink/60 hover:border-tora hover:text-tora"
                }`}
              >
                {cat.name}
              </button>
            );
          })}
        </div>
      )}
      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <EmptyState icon="🎥" title="公開コンテンツはまだありません" />
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {items.map((c) => {
            const thumb = uploadsUrl(c.thumbnailKey);
            const typeLabel =
              CONTENT_TYPE_LABELS[c.contentType] ?? c.contentType;
            return (
              <li key={c.id} className="h-full">
                <Link
                  to={`/contents/${c.id}`}
                  className="card h-full flex flex-col overflow-hidden group transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tora"
                >
                  {/* サムネ */}
                  <div className="relative aspect-[16/10] bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden">
                    {thumb ? (
                      <img
                        src={thumb}
                        alt=""
                        className="w-full h-full object-cover object-top md:group-hover:scale-[1.03] transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-tora-ink/30 text-lg tracking-widest">
                        準備中
                      </div>
                    )}
                    {c.contentType === "video" && thumb && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="w-12 h-12 rounded-full bg-black/50 text-white flex items-center justify-center text-xl">
                          ▶
                        </span>
                      </div>
                    )}
                    {/* 鍵アイコン (会員限定) */}
                    <div className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center text-tora-ink/60 shadow-sm">
                      🔒
                    </div>
                  </div>
                  {/* テキスト */}
                  <div className="flex-1 flex flex-col p-4 md:p-5">
                    <h3 className="text-base md:text-lg font-bold text-tora-ink leading-snug group-hover:text-tora transition-colors line-clamp-2">
                      {c.title}
                    </h3>
                    <div className="mt-auto pt-3 flex flex-wrap gap-2">
                      {catName(c.categoryCode) && (
                        <span className="inline-block rounded-full bg-tora/10 text-tora text-xs px-3 py-1 font-semibold">
                          {catName(c.categoryCode)}
                        </span>
                      )}
                      <span className="inline-block rounded-full border border-tora text-tora text-xs px-3 py-1 font-medium">
                        {typeLabel}
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
